import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dynamic Cards Management Component (Materials & Capabilities)
const DynamicCardsManagement = () => {
  const theme = useThemeClasses();
  const [materialsCard, setMaterialsCard] = useState({ title: 'Materials', show_on_homepage: true, icon_name: 'cube' });
  const [capabilitiesCard, setCapabilitiesCard] = useState({ title: 'Capabilities', show_on_homepage: true, icon_name: 'shield' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const iconOptions = [
    { value: 'printer', label: '🖨️ 3D Printer' },
    { value: 'lightning', label: '⚡ Lightning Bolt' },
    { value: 'lightbulb', label: '💡 Lightbulb' },
    { value: 'factory', label: '🏭 Factory' },
    { value: 'cube', label: '📦 Cube' },
    { value: 'shield', label: '🛡️ Shield' },
    { value: 'cog', label: '⚙️ Settings' },
    { value: 'tools', label: '🔧 Tools' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings/public`);
      if (response.data.materials_card) {
        setMaterialsCard(response.data.materials_card);
      }
      if (response.data.capabilities_card) {
        setCapabilitiesCard(response.data.capabilities_card);
      }
    } catch (error) {
      console.error('Failed to load dynamic card settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/dynamic-cards`, {
        materials_card: materialsCard,
        capabilities_card: capabilitiesCard
      });
      toast.success('Dynamic cards updated successfully!');
    } catch (error) {
      console.error('Failed to save dynamic cards:', error);
      toast.error('Failed to save dynamic cards');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <div className="flex items-center justify-center">
          <div className={`${theme.textSecondary}`}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme.card} p-6 rounded-lg shadow`}>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h4 className={`font-semibold ${theme.textPrimary}`}>📦 Materials & Capabilities Cards</h4>
          <p className={`text-sm ${theme.textSecondary}`}>Customize how materials and capabilities appear in the services section</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Materials Card Settings */}
        <div className={`border ${theme.border} rounded-lg p-4`}>
          <h5 className={`font-semibold ${theme.textPrimary} mb-4`}>Materials Card</h5>
          
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  checked={materialsCard.show_on_homepage}
                  onChange={(e) => setMaterialsCard({ ...materialsCard, show_on_homepage: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <span className={`text-sm font-medium ${theme.textPrimary}`}>Show on homepage</span>
              </label>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textPrimary} mb-1`}>Card Title</label>
              <input
                type="text"
                value={materialsCard.title}
                onChange={(e) => setMaterialsCard({ ...materialsCard, title: e.target.value })}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Materials"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textPrimary} mb-1`}>Icon</label>
              <select
                value={materialsCard.icon_name}
                onChange={(e) => setMaterialsCard({ ...materialsCard, icon_name: e.target.value })}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                {iconOptions.map(icon => (
                  <option key={icon.value} value={icon.value}>{icon.label}</option>
                ))}
              </select>
            </div>

            <div className={`p-3 ${theme.bgSecondary} rounded border ${theme.border}`}>
              <p className={`text-xs ${theme.textSecondary}`}>
                <strong>Preview:</strong> This card will display your active materials as a bullet list in the services section.
              </p>
            </div>
          </div>
        </div>

        {/* Capabilities Card Settings */}
        <div className={`border ${theme.border} rounded-lg p-4`}>
          <h5 className={`font-semibold ${theme.textPrimary} mb-4`}>Capabilities Card</h5>
          
          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-2 mb-3">
                <input
                  type="checkbox"
                  checked={capabilitiesCard.show_on_homepage}
                  onChange={(e) => setCapabilitiesCard({ ...capabilitiesCard, show_on_homepage: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <span className={`text-sm font-medium ${theme.textPrimary}`}>Show on homepage</span>
              </label>
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textPrimary} mb-1`}>Card Title</label>
              <input
                type="text"
                value={capabilitiesCard.title}
                onChange={(e) => setCapabilitiesCard({ ...capabilitiesCard, title: e.target.value })}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="Capabilities"
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${theme.textPrimary} mb-1`}>Icon</label>
              <select
                value={capabilitiesCard.icon_name}
                onChange={(e) => setCapabilitiesCard({ ...capabilitiesCard, icon_name: e.target.value })}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                {iconOptions.map(icon => (
                  <option key={icon.value} value={icon.value}>{icon.label}</option>
                ))}
              </select>
            </div>

            <div className={`p-3 ${theme.bgSecondary} rounded border ${theme.border}`}>
              <p className={`text-xs ${theme.textSecondary}`}>
                <strong>Preview:</strong> This card will display your active capabilities as a bullet list in the services section.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-700">
          <strong>💡 Note:</strong> These cards only appear when you have active materials or capabilities added in their respective sections below. The bullet lists are automatically generated from your data.
        </p>
      </div>
    </div>
  );
};

// Service Cards Management Component
const ServiceCardsManagement = () => {
  const theme = useThemeClasses();
  const [serviceCards, setServiceCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCard, setEditingCard] = useState(null);

  const iconOptions = [
    { value: 'printer', label: '🖨️ 3D Printer', svg: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z' },
    { value: 'lightning', label: '⚡ Lightning Bolt', svg: 'M13 10V3L4 14h7v7l9-11h-7z' },
    { value: 'lightbulb', label: '💡 Lightbulb', svg: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
    { value: 'factory', label: '🏭 Factory', svg: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
    { value: 'cube', label: '📦 Cube', svg: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
    { value: 'shield', label: '🛡️ Shield', svg: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    { value: 'cog', label: '⚙️ Settings', svg: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
    { value: 'tools', label: '🔧 Tools', svg: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' }
  ];

  useEffect(() => {
    loadServiceCards();
  }, []);

  const loadServiceCards = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/settings/public`);
      const cards = response.data.service_cards || [];
      setServiceCards(cards.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Failed to load service cards:', error);
      toast.error('Failed to load service cards');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/service-cards`, serviceCards);
      toast.success('Service cards updated successfully!');
      setEditingCard(null);
    } catch (error) {
      console.error('Failed to save service cards:', error);
      toast.error('Failed to save service cards');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCard = (index, field, value) => {
    const updatedCards = [...serviceCards];
    updatedCards[index] = { ...updatedCards[index], [field]: value };
    setServiceCards(updatedCards);
  };

  const handleAddBullet = (index) => {
    const updatedCards = [...serviceCards];
    const bullets = updatedCards[index].bullet_points || [];
    updatedCards[index] = { ...updatedCards[index], bullet_points: [...bullets, ''] };
    setServiceCards(updatedCards);
  };

  const handleUpdateBullet = (cardIndex, bulletIndex, value) => {
    const updatedCards = [...serviceCards];
    const bullets = [...(updatedCards[cardIndex].bullet_points || [])];
    bullets[bulletIndex] = value;
    updatedCards[cardIndex] = { ...updatedCards[cardIndex], bullet_points: bullets };
    setServiceCards(updatedCards);
  };

  const handleRemoveBullet = (cardIndex, bulletIndex) => {
    const updatedCards = [...serviceCards];
    const bullets = (updatedCards[cardIndex].bullet_points || []).filter((_, i) => i !== bulletIndex);
    updatedCards[cardIndex] = { ...updatedCards[cardIndex], bullet_points: bullets };
    setServiceCards(updatedCards);
  };

  const handleAddCard = () => {
    const newCard = {
      id: `card-${Date.now()}`,
      title: 'New Service',
      description: '',
      bullet_points: [],
      icon_name: 'default',
      order: serviceCards.length + 1,
      is_active: true
    };
    setServiceCards([...serviceCards, newCard]);
    setEditingCard(serviceCards.length); // Open edit mode for new card
  };

  const handleRemoveCard = (index) => {
    if (window.confirm('Are you sure you want to remove this service card?')) {
      const updatedCards = serviceCards.filter((_, i) => i !== index);
      setServiceCards(updatedCards);
      if (editingCard === index) {
        setEditingCard(null);
      }
    }
  };

  const handleToggleActive = (index) => {
    const updatedCards = [...serviceCards];
    updatedCards[index] = { ...updatedCards[index], is_active: !updatedCards[index].is_active };
    setServiceCards(updatedCards);
  };

  if (loading) {
    return (
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <div className="flex items-center justify-center">
          <div className={`${theme.textSecondary}`}>Loading service cards...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme.card} p-6 rounded-lg shadow`}>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h4 className={`font-semibold ${theme.textPrimary}`}>🎨 Service Cards</h4>
          <p className={`text-sm ${theme.textSecondary}`}>Customize all service cards displayed on the homepage (including Materials and Capabilities)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleAddCard}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
          >
            <span>➕</span>
            <span>Add Card</span>
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {serviceCards.map((card, index) => (
          <div key={card.id || index} className={`border ${theme.border} rounded-lg p-4`}>
            <div className="flex items-start gap-3 mb-3">
              <div className={`${card.is_active ? 'bg-indigo-100 dark:bg-indigo-900/30' : 'bg-gray-200 dark:bg-gray-700'} p-2 rounded-lg`}>
                <svg className={`w-6 h-6 ${card.is_active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconOptions.find(i => i.value === card.icon_name)?.svg || iconOptions[0].svg} />
                </svg>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${theme.textSecondary}`}>Card {index + 1}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      card.is_active 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {card.is_active ? '✓ Active' : '✕ Disabled'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(index)}
                      className={`text-xs px-2 py-1 rounded ${
                        card.is_active 
                          ? 'text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20' 
                          : 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                      }`}
                      title={card.is_active ? 'Disable card' : 'Enable card'}
                    >
                      {card.is_active ? '🔇' : '🔊'}
                    </button>
                    <button
                      onClick={() => setEditingCard(editingCard === index ? null : index)}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {editingCard === index ? '✓ Done' : '✏️ Edit'}
                    </button>
                    <button
                      onClick={() => handleRemoveCard(index)}
                      className="text-xs text-red-600 hover:text-red-700"
                      title="Remove card"
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {editingCard === index ? (
                  <div className="space-y-3">
                    <div>
                      <label className={`block text-xs font-medium ${theme.textPrimary} mb-1`}>Title</label>
                      <input
                        type="text"
                        value={card.title}
                        onChange={(e) => handleUpdateCard(index, `title`, e.target.value)}
                        className={`w-full px-2 py-1 text-sm border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium ${theme.textPrimary} mb-1`}>Description (optional)</label>
                      <textarea
                        value={card.description || ''}
                        onChange={(e) => handleUpdateCard(index, `description`, e.target.value)}
                        rows={2}
                        placeholder="Add a description..."
                        className={`w-full px-2 py-1 text-xs border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                      />
                    </div>
                    
                    {/* Bullet Points */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className={`block text-xs font-medium ${theme.textPrimary}`}>Bullet Points (optional)</label>
                        <button
                          type="button"
                          onClick={() => handleAddBullet(index)}
                          className="text-xs text-green-600 hover:text-green-700"
                        >
                          + Add Bullet
                        </button>
                      </div>
                      {(card.bullet_points || []).length > 0 && (
                        <div className="space-y-2">
                          {(card.bullet_points || []).map((bullet, bulletIdx) => (
                            <div key={bulletIdx} className="flex gap-2">
                              <span className={`text-xs ${theme.textSecondary} mt-1`}>•</span>
                              <input
                                type="text"
                                value={bullet}
                                onChange={(e) => handleUpdateBullet(index, bulletIdx, e.target.value)}
                                placeholder="Bullet point text"
                                className={`flex-1 px-2 py-1 text-xs border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveBullet(index, bulletIdx)}
                                className="text-red-500 hover:text-red-700 text-xs px-2"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {(!card.bullet_points || card.bullet_points.length === 0) && (
                        <p className={`text-xs ${theme.textSecondary} italic`}>No bullet points yet</p>
                      )}
                    </div>
                    
                    <div>
                      <label className={`block text-xs font-medium ${theme.textPrimary} mb-1`}>Icon</label>
                      <select
                        value={card.icon_name}
                        onChange={(e) => handleUpdateCard(index, `icon_name`, e.target.value)}
                        className={`w-full px-2 py-1 text-sm border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme.bgPrimary} ${theme.textPrimary}`}
                      >
                        {iconOptions.map(icon => (
                          <option key={icon.value} value={icon.value}>{icon.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h5 className={`font-semibold ${theme.textPrimary} mb-1`}>{card.title}</h5>
                    {card.description && (
                      <p className={`text-xs ${theme.textSecondary} mb-2`}>{card.description}</p>
                    )}
                    {card.bullet_points && card.bullet_points.length > 0 && (
                      <ul className={`text-xs ${theme.textSecondary} space-y-1`}>
                        {card.bullet_points.slice(0, 3).map((bullet, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="mr-1">•</span>
                            <span className="line-clamp-1">{bullet}</span>
                          </li>
                        ))}
                        {card.bullet_points.length > 3 && (
                          <li className="text-xs italic">+{card.bullet_points.length - 3} more...</li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className={`text-sm ${theme.textPrimary}`}>
          <strong>💡 Tip:</strong> All service cards appear in the "Our Services" section on the homepage. Edit any card including Materials and Capabilities directly here. The changes appear instantly on your website.
        </p>
      </div>
    </div>
  );
};

// Comprehensive Website Design Management Component
export const WebsiteCustomizationManagement = () => {
  const theme = useThemeClasses();
  const [customization, setCustomization] = useState({
    primary_color: '#667eea',
    secondary_color: '#764ba2',
    accent_color: '#4caf50',
    text_primary: '#333333',
    text_secondary: '#666666',
    background_primary: '#ffffff',
    background_secondary: '#f8f9fa',
    theme_name: 'light',
    dark_mode: false,
    enable_theme_switcher: true,
    dark_primary_color: '#a78bfa',
    dark_secondary_color: '#c084fc',
    dark_accent_color: '#60a5fa',
    dark_text_primary: '#f3f4f6',
    dark_text_secondary: '#d1d5db',
    dark_background_primary: '#111827',
    dark_background_secondary: '#1f2937',
    custom_css: '',
    primary_font: 'Arial, sans-serif',
    heading_font: 'Arial, sans-serif',
    header_style: 'default',
    footer_style: 'default'
  });
  const [companySettings, setCompanySettings] = useState({
    company_name: 'RG-3D',
    company_description: 'Premium 3D Printed & Personalized Items',
    logo_url: '',  // Logo should be uploaded via admin settings
    favicon_url: '',
    social_media_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: '',
      tiktok: ''
    }
  });
  const [privilegedUsers, setPrivilegedUsers] = useState([]);
  const [roleDefinitions, setRoleDefinitions] = useState([]);
  const [showAddPrivilegedUser, setShowAddPrivilegedUser] = useState(false);
  const [editingPrivilegedUser, setEditingPrivilegedUser] = useState(null);
  const [newPrivilegedUser, setNewPrivilegedUser] = useState({
    email: '',
    role: 'staff'
  });
  const [themePresets, setThemePresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    try {
      await Promise.all([
        loadCustomization(),
        loadCompanySettings(),
        loadThemePresets(),
        loadPrivilegedUsers(),
        loadRoleDefinitions()
      ]);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load website design settings');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomization = async () => {
    try {
      const response = await axios.get(`${API}/admin/customization`);
      setCustomization(response.data);
    } catch (error) {
      console.error('Failed to load customization:', error);
    }
  };

  const loadCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data?.company_settings) {
        setCompanySettings(response.data.company_settings);
      }
    } catch (error) {
      console.error('Failed to load company settings:', error);
    }
  };

  const loadPrivilegedUsers = async () => {
    try {
      // Get users with staff level and above
      const response = await axios.get(`${API}/admin/customers`);
      const allUsers = response.data || [];
      
      // Filter for privileged users (staff, admin, super_admin)
      const privileged = allUsers.filter(user => 
        user.role && ['staff', 'admin', 'super_admin'].includes(user.role)
      );
      
      setPrivilegedUsers(privileged);
    } catch (error) {
      console.error('Failed to load privileged users:', error);
    }
  };

  const loadRoleDefinitions = async () => {
    try {
      const response = await axios.get(`${API}/admin/roles`);
      setRoleDefinitions(response.data?.data?.roles || []);
    } catch (error) {
      console.error('Failed to load role definitions:', error);
      // Fallback role definitions
      setRoleDefinitions([
        {
          id: 'customer',
          name: 'Customer',
          description: 'Regular customer with shopping privileges',
          permissions: ['view_products', 'create_orders', 'manage_own_profile'],
          level: 0
        },
        {
          id: 'staff',
          name: 'Staff',
          description: 'Staff member with limited admin access',
          permissions: ['view_products', 'view_orders', 'update_order_status'],
          level: 1
        },
        {
          id: 'admin',
          name: 'Administrator',
          description: 'Full admin access to manage store',
          permissions: ['full_access', 'manage_products', 'manage_orders', 'manage_customers'],
          level: 2
        },
        {
          id: 'super_admin',
          name: 'Super Administrator',
          description: 'Unrestricted access to all system functions',
          permissions: ['unrestricted_access'],
          level: 3
        }
      ]);
    }
  };

  const loadThemePresets = async () => {
    try {
      const response = await axios.get(`${API}/admin/theme-presets`);
      setThemePresets(response.data);
    } catch (error) {
      // Use fallback presets if endpoint not available
      setThemePresets([
        {
          id: 'modern-blue',
          name: 'Modern Blue',
          description: 'Professional blue theme',
          primary_color: '#667eea',
          secondary_color: '#764ba2',
          accent_color: '#4caf50'
        },
        {
          id: 'green-nature',
          name: 'Green Nature',
          description: 'Natural green theme',
          primary_color: '#4caf50',
          secondary_color: '#8bc34a',
          accent_color: '#ff9800'
        }
      ]);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      const logoUrl = response.data.file_url;
      setCompanySettings(prev => ({
        ...prev,
        logo_url: logoUrl
      }));
      
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // Save customization
      await axios.put(`${API}/admin/customization`, customization);
      
      // Save company settings
      await axios.put(`${API}/settings`, {
        company_settings: companySettings
      });
      
      toast.success('Website design settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save website design settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePrivilegedUserRoleChange = async (userId, newRole) => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/customers/${userId}/role`, { role: newRole });
      toast.success('User role updated successfully');
      loadPrivilegedUsers();
    } catch (error) {
      console.error('Failed to update user role:', error);
      toast.error('Failed to update user role');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPrivilegedUser = async () => {
    try {
      setSaving(true);
      
      // First check if user exists
      const userResponse = await axios.get(`${API}/admin/customers`);
      const allUsers = userResponse.data || [];
      const existingUser = allUsers.find(user => user.email === newPrivilegedUser.email);
      
      if (!existingUser) {
        toast.error('User not found. User must have an account first.');
        return;
      }
      
      // Update user role
      await axios.put(`${API}/admin/customers/${existingUser.id}/role`, { 
        role: newPrivilegedUser.role 
      });
      
      toast.success('User promoted to privileged access successfully');
      setNewPrivilegedUser({ email: '', role: 'staff' });
      setShowAddPrivilegedUser(false);
      loadPrivilegedUsers();
    } catch (error) {
      console.error('Failed to add privileged user:', error);
      toast.error('Failed to add privileged user');
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePrivilegedAccess = async (userId, userEmail) => {
    if (!window.confirm(`Remove privileged access for ${userEmail}? They will be demoted to Customer role.`)) {
      return;
    }

    try {
      setSaving(true);
      await axios.put(`${API}/admin/customers/${userId}/role`, { role: 'customer' });
      toast.success('Privileged access removed successfully');
      loadPrivilegedUsers();
    } catch (error) {
      console.error('Failed to remove privileged access:', error);
      toast.error('Failed to remove privileged access');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyPreset = async (preset) => {
    try {
      setSaving(true);
      const updatedCustomization = {
        ...customization,
        primary_color: preset.primary_color,
        secondary_color: preset.secondary_color,
        accent_color: preset.accent_color,
        text_primary: preset.text_primary || '#333333',
        text_secondary: preset.text_secondary || '#666666',
        background_primary: preset.background_primary || '#ffffff',
        background_secondary: preset.background_secondary || '#f8f9fa',
        theme_name: preset.name
      };
      
      setCustomization(updatedCustomization);
      await axios.put(`${API}/admin/customization`, updatedCustomization);
      toast.success(`Theme preset "${preset.name}" applied successfully!`);
    } catch (error) {
      console.error('Failed to apply theme preset:', error);
      toast.error('Failed to apply theme preset');
    } finally {
      setSaving(false);
    }
  };

  const renderBrandingTab = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Company Branding</h3>
      
      {/* Company Information */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h4 className={`font-semibold mb-4 ${theme.textPrimary}`}>Company Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Company Name</label>
            <input
              type="text"
              value={companySettings.company_name}
              onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
              className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="RG-3D"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Company Description</label>
            <input
              type="text"
              value={companySettings.company_description}
              onChange={(e) => setCompanySettings({...companySettings, company_description: e.target.value})}
              className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Premium 3D Printed & Personalized Items"
            />
          </div>
        </div>
      </div>

      {/* Logo Management */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h4 className={`font-semibold mb-4 ${theme.textPrimary}`}>Logo & Branding</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Current Logo</label>
            <div className="flex items-center space-x-4">
              {companySettings.logo_url ? (
                <div className="relative flex-shrink-0">
                  <img
                    src={companySettings.logo_url.startsWith('http') 
                      ? companySettings.logo_url 
                      : `${process.env.REACT_APP_BACKEND_URL}${companySettings.logo_url}`}
                    alt="Company Logo"
                    className={`w-24 h-24 object-contain border-2 ${theme.border} rounded-lg p-2 bg-white dark:bg-gray-800 shadow-sm`}
                    onLoad={(e) => {
                      // Hide image and show placeholder if it's too small (likely corrupted/placeholder)
                      if (e.target.naturalWidth < 10 || e.target.naturalHeight < 10) {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="hidden w-24 h-24 items-center justify-center border-2 border-dashed border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex-col">
                    <span className="text-3xl mb-1">⚠️</span>
                    <span className="text-xs text-yellow-700 dark:text-yellow-400 text-center px-2">Logo file too small or corrupted</span>
                  </div>
                </div>
              ) : (
                <div className="w-24 h-24 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <span className="text-3xl">🖼️</span>
                </div>
              )}
              <div className="flex-1">
                <input
                  type="url"
                  value={companySettings.logo_url}
                  onChange={(e) => setCompanySettings({...companySettings, logo_url: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="https://example.com/logo.png or upload below"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>
                  Current: {companySettings.logo_url ? companySettings.logo_url.split('/').pop() : 'No logo set'}
                </p>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Upload New Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files[0]) {
                  handleLogoUpload(e.target.files[0]);
                }
              }}
              className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              disabled={uploadingLogo}
            />
            {uploadingLogo && <p className="text-sm text-blue-600 mt-1">Uploading logo...</p>}
          </div>
        </div>
      </div>

      {/* Social Media Links */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h4 className={`font-semibold mb-4 ${theme.textPrimary}`}>Social Media Links</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(companySettings.social_media_links || {}).map(([platform, url]) => (
            <div key={platform}>
              <label className="block text-sm font-medium mb-2 capitalize">
                {platform} {platform === 'facebook' && '📘'} {platform === 'twitter' && '🐦'} 
                {platform === 'instagram' && '📷'} {platform === 'linkedin' && '💼'} 
                {platform === 'youtube' && '📺'} {platform === 'tiktok' && '🎵'}
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setCompanySettings({
                  ...companySettings,
                  social_media_links: {
                    ...companySettings.social_media_links,
                    [platform]: e.target.value
                  }
                })}
                className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder={`https://${platform}.com/your-page`}
              />
            </div>
          ))}
        </div>
        
        <div className="mt-4 p-4 bg-blue-50 rounded border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>💡 Tip:</strong> Social media links will appear in the sharing buttons on your homepage and checkout page. 
            Leave blank to hide specific platforms.
          </p>
        </div>
      </div>
    </div>
  );

  const renderColorsTab = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Color Scheme & Themes</h3>
      
      {/* Theme Mode Selection (Light/Dark) */}
      <div className={`${theme.card} p-6 rounded-lg shadow mb-6`}>
        <h4 className={`font-semibold mb-4 ${theme.textPrimary}`}>🌓 Default Theme Mode</h4>
        <p className={`text-sm ${theme.textSecondary} mb-4`}>
          Choose the default theme mode for your website. Users can still toggle between light and dark modes using the theme switcher in the header.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Light Theme */}
          <div 
            onClick={() => setCustomization({...customization, theme_name: 'light', dark_mode: false})}
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              customization.theme_name === 'light' 
                ? 'border-blue-600 bg-blue-50' 
                : `${theme.border} hover:border-blue-300`
            }`}
          >
            <div className="flex items-center justify-center mb-3">
              <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h5 className="font-semibold text-center mb-2">Light Mode</h5>
            <p className={`text-sm ${theme.textSecondary} text-center`}>Clean, bright, professional</p>
            {customization.theme_name === 'light' && (
              <div className="mt-3 text-center">
                <span className="inline-block bg-blue-600 text-white text-xs px-3 py-1 rounded-full">
                  ✓ Active
                </span>
              </div>
            )}
          </div>

          {/* Dark Theme */}
          <div 
            onClick={() => setCustomization({...customization, theme_name: 'dark', dark_mode: true})}
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              customization.theme_name === 'dark' 
                ? 'border-purple-600 bg-purple-50' 
                : `${theme.border} hover:border-purple-300`
            }`}
          >
            <div className="flex items-center justify-center mb-3">
              <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            </div>
            <h5 className="font-semibold text-center mb-2">Dark Mode</h5>
            <p className={`text-sm ${theme.textSecondary} text-center`}>Modern, sleek, eye-friendly</p>
            {customization.theme_name === 'dark' && (
              <div className="mt-3 text-center">
                <span className="inline-block bg-purple-600 text-white text-xs px-3 py-1 rounded-full">
                  ✓ Active
                </span>
              </div>
            )}
          </div>

          {/* Auto Theme */}
          <div 
            onClick={() => setCustomization({...customization, theme_name: 'auto', dark_mode: false})}
            className={`border-2 rounded-lg p-6 cursor-pointer transition-all ${
              customization.theme_name === 'auto' 
                ? 'border-indigo-600 bg-indigo-50' 
                : `${theme.border} hover:border-indigo-300`
            }`}
          >
            <div className="flex items-center justify-center mb-3">
              <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h5 className="font-semibold text-center mb-2">System Auto</h5>
            <p className={`text-sm ${theme.textSecondary} text-center`}>Follows user's system preference</p>
            {customization.theme_name === 'auto' && (
              <div className="mt-3 text-center">
                <span className="inline-block bg-indigo-600 text-white text-xs px-3 py-1 rounded-full">
                  ✓ Active
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Theme Switcher Toggle */}
        <div className={`mt-6 p-4 ${theme.bgSecondary} rounded-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <h5 className="font-semibold mb-1">Enable Theme Switcher</h5>
              <p className={`text-sm ${theme.textSecondary}`}>Allow users to toggle between light and dark modes</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={customization.enable_theme_switcher !== false}
                onChange={(e) => setCustomization({...customization, enable_theme_switcher: e.target.checked})}
                className="sr-only peer"
              />
              <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600`}></div>
            </label>
          </div>
        </div>
      </div>

      {/* Theme Presets */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h4 className={`font-semibold mb-4 ${theme.textPrimary}`}>Quick Theme Presets</h4>
        <p className={`text-sm ${theme.textSecondary} mb-4`}>
          Apply pre-designed color schemes to quickly customize your brand`s appearance
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {themePresets.map((preset) => (
            <div key={preset.id} className={`border ${theme.border} rounded-lg overflow-hidden hover:shadow-md transition-shadow`}>
              <div className="p-4">
                <div 
                  className="h-16 rounded mb-3"
                  style={{
                    background: `linear-gradient(135deg, ${preset.primary_color} 0%, ${preset.secondary_color} 100%)`
                  }}
                />
                <h5 className="font-semibold mb-2">{preset.name}</h5>
                <p className={`text-sm ${theme.textSecondary} mb-3`}>{preset.description}</p>
                <button
                  onClick={() => handleApplyPreset(preset)}
                  disabled={saving}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Apply Theme
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Color Configuration */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h4 className={`font-semibold mb-4 ${theme.textPrimary}`}>Custom Colors</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Primary Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={customization.primary_color}
                onChange={(e) => setCustomization({...customization, primary_color: e.target.value})}
                className={`w-12 h-12 border ${theme.border} rounded cursor-pointer`}
              />
              <input
                type="text"
                value={customization.primary_color}
                onChange={(e) => setCustomization({...customization, primary_color: e.target.value})}
                className={`flex-1 px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Secondary Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={customization.secondary_color}
                onChange={(e) => setCustomization({...customization, secondary_color: e.target.value})}
                className={`w-12 h-12 border ${theme.border} rounded cursor-pointer`}
              />
              <input
                type="text"
                value={customization.secondary_color}
                onChange={(e) => setCustomization({...customization, secondary_color: e.target.value})}
                className={`flex-1 px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Accent Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={customization.accent_color}
                onChange={(e) => setCustomization({...customization, accent_color: e.target.value})}
                className={`w-12 h-12 border ${theme.border} rounded cursor-pointer`}
              />
              <input
                type="text"
                value={customization.accent_color}
                onChange={(e) => setCustomization({...customization, accent_color: e.target.value})}
                className={`flex-1 px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContentTab = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">Homepage Content Management</h3>
      
      {/* Service Cards Management */}
      <ServiceCardsManagement />
    </div>
  );

  const renderUserRolesTab = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold mb-4">👥 User Role Management</h3>
      
      {/* Role Definitions */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <h4 className={`font-semibold mb-4 ${theme.textPrimary}`}>Role Definitions & Permissions</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {roleDefinitions.map(role => (
            <div key={role.id} className={`border ${theme.border} rounded-lg p-4`}>
              <div className="flex items-center space-x-3 mb-3">
                <div className={`w-3 h-3 rounded-full ${
                  role.id === 'customer' ? 'bg-blue-500' :
                  role.id === 'staff' ? 'bg-green-500' :
                  role.id === 'admin' ? 'bg-purple-500' :
                  'bg-red-500'
                }`}></div>
                <div>
                  <h5 className={`font-semibold ${theme.textPrimary}`}>{role.name}</h5>
                  <p className={`text-xs ${theme.textSecondary}`}>Level {role.level}</p>
                </div>
              </div>
              <p className={`text-sm ${theme.textSecondary} mb-3`}>{role.description}</p>
              <div className={`${theme.bgSecondary} p-2 rounded`}>
                <p className={`text-xs ${theme.textSecondary} font-medium mb-1`}>Key Permissions:</p>
                <ul className={`text-xs ${theme.textSecondary}`}>
                  {role.permissions.slice(0, 3).map((permission, index) => (
                    <li key={index}>• {permission.replace('_', ' ')}</li>
                  ))}
                  {role.permissions.length > 3 && (
                    <li>• ... and {role.permissions.length - 3} more</li>
                  )}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Privileged Users Management */}
      <div className={`${theme.card} p-6 rounded-lg shadow`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h4 className={`font-semibold ${theme.textPrimary}`}>Privileged Users (Staff & Above)</h4>
            <p className={`text-sm ${theme.textSecondary}`}>Manage users with elevated access levels</p>
          </div>
          <button
            onClick={() => setShowAddPrivilegedUser(!showAddPrivilegedUser)}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            {showAddPrivilegedUser ? 'Cancel' : 'Promote User'}
          </button>
        </div>

        {/* Add Privileged User Form */}
        {showAddPrivilegedUser && (
          <div className="bg-green-50 p-4 rounded-lg mb-6 border border-green-200">
            <h5 className="font-semibold mb-3 text-green-800">Promote User to Privileged Access</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">User Email</label>
                <input
                  type="email"
                  value={newPrivilegedUser.email}
                  onChange={(e) => setNewPrivilegedUser({...newPrivilegedUser, email: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-green-500`}
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Assign Role</label>
                <select
                  value={newPrivilegedUser.role}
                  onChange={(e) => setNewPrivilegedUser({...newPrivilegedUser, role: e.target.value})}
                  className={`w-full px-3 py-2 border ${theme.border} rounded focus:outline-none focus:ring-2 focus:ring-green-500`}
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
            </div>
            <div className="flex space-x-4 mt-4">
              <button
                onClick={handleAddPrivilegedUser}
                disabled={saving || !newPrivilegedUser.email}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Promoting...' : 'Promote User'}
              </button>
              <button
                onClick={() => setShowAddPrivilegedUser(false)}
                className={`${theme.bgSecondary}0 text-white px-4 py-2 rounded hover:bg-gray-600`}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Privileged Users Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`${theme.bgSecondary}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>User</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Email</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Current Role</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Last Login</th>
                <th className={`px-6 py-3 text-left text-xs font-medium ${theme.textSecondary} uppercase tracking-wider`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`${theme.card} divide-y divide-gray-200`}>
              {privilegedUsers.map((user) => (
                <tr key={user.id} className={`hover:bg-gray-100 dark:hover:bg-gray-700`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 flex-shrink-0">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          user.role === 'staff' ? 'bg-green-100' :
                          user.role === 'admin' ? 'bg-purple-100' :
                          'bg-red-100'
                        }`}>
                          <span className={`text-xs font-bold ${
                            user.role === 'staff' ? 'text-green-600' :
                            user.role === 'admin' ? 'text-purple-600' :
                            'text-red-600'
                          }`}>
                            {user.role === 'staff' ? '👨‍💼' : user.role === 'admin' ? '⚙️' : '👑'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${theme.textPrimary}`}>{user.name}</div>
                        <div className={`text-xs ${theme.textSecondary}`}>ID: {user.id?.slice(-8)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm ${theme.textPrimary}`}>{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.role || 'customer'}
                      onChange={(e) => handlePrivilegedUserRoleChange(user.id, e.target.value)}
                      disabled={saving}
                      className={`px-3 py-1 text-sm font-medium rounded-full border-0 cursor-pointer ${
                        user.role === 'staff' ? 'bg-green-100 text-green-800' :
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                        'bg-red-100 text-red-800'
                      } disabled:opacity-50`}
                    >
                      <option value="staff">Staff</option>
                      <option value="admin">Administrator</option>
                      <option value="super_admin">Super Administrator</option>
                    </select>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${theme.textSecondary}`}>
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : `Never`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleRemovePrivilegedAccess(user.id, user.email)}
                      disabled={saving}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50"
                    >
                      Remove Access
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {privilegedUsers.length === 0 && (
            <div className={`text-center py-8 ${theme.textSecondary}`}>
              <div className="text-4xl mb-4">👥</div>
              <p className="text-lg mb-2">No privileged users found</p>
              <p className="text-sm mb-6">Users with staff level access and above will appear here</p>
              <button
                onClick={() => setShowAddPrivilegedUser(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
              >
                Promote Your First User
              </button>
            </div>
          )}
        </div>

        {/* Role Management Guidelines */}
        <div className="mt-6 bg-blue-50 p-4 rounded border border-blue-200">
          <h5 className="font-semibold text-blue-800 mb-2">🛡️ Role Management Guidelines</h5>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Staff:</strong> Can view and update orders, limited admin access</li>
            <li>• <strong>Administrator:</strong> Full store management, all admin features</li>
            <li>• <strong>Super Administrator:</strong> Unrestricted system access (use carefully)</li>
            <li>• Users must have an account before they can be promoted to privileged access</li>
            <li>• All role changes are logged and monitored for security</li>
          </ul>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className={`${theme.textSecondary}`}>Loading website design settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Website Design & Content</h2>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className={`border-b ${theme.border}`}>
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'branding', label: 'Branding & Logo', icon: '🏢' },
            { id: 'colors', label: 'Colors & Themes', icon: '🎨' },
            { id: 'content', label: 'Homepage Content', icon: '📄' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'branding' && renderBrandingTab()}
        {activeTab === 'colors' && renderColorsTab()}
        {activeTab === 'content' && renderContentTab()}
      </div>
    </div>
  );
};

export default WebsiteCustomizationManagement;